import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  ChangeDetectorRef,
  TemplateRef,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { combineLatest, finalize, Subject, take, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from '../../../../../components/confirm-dialog/confirm-dialog.component';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { NoDataComponent } from '../../../../../pages/content/no-data/no-data.component';
import { FbaAssessmentComponent } from '../assessment-fba/fba-assessment/fba-assessment.component';
import { InsightsDataComponent } from '../../insights-data/insights-data.component';
import { CommonService } from '../../../../../shared/services/common.service';
import { InsightMode, BehaviorStatus } from '../../../insights.enums';
import { LoaderService } from '../../../../../core/services/loader.service';
import {
  BehaviorFunction,
  CaseBehaviour,
  CaseDetailModel,
  ManageStudentDrawerParams,
} from '../../../insights.interface';
import { DefineBehaviorComponent } from '../define-behavior/define-behavior.component';
import { BehaviorService } from '../define-behavior/define-behavior.service';
import { DefineBehaviorWizardComponent } from '../define-behavior-wizard/define-behavior-wizard.component';
import { InsightsService } from '../../../insights.service';
import { ScreenerResultsGraphComponent } from '../assessment-fba/screener-results-graph/screener-results-graph.component';
import {
  NzBreadCrumbComponent,
  NzBreadCrumbItemComponent,
} from 'ng-zorro-antd/breadcrumb';
import { ManageBehaviorStrategiesComponent } from '../manage-behavior-strategies/manage-behavior-strategies.component';
import { FunctionalAssessmentModelAnswer } from '../assessment-fba/assesment-fba.interface';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { LabelKey } from '../../../../../core/models/report-labels.model';
import { ReportLabelPipe } from '../../../../../core/pipes/report-label.pipe';
import { ReportLabelsService } from '../../../../manage-settings/report-labels/report-labels.service';
import { FidelityCheckCorrectionNeededComponent } from '../../../../administration-fidelity-check/fidelity-check-drawer/fidelity-check-correction-needed/fidelity-check-correction-needed.component';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { HelpTextComponent } from '../../../../../components/help-text/help-text.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TimeZoneService } from '../../../../../core/services/timezone.service';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import {
  BipTemplateGatingService,
  BipTileSection,
} from '../../../../../shared/services/bip-template-gating.service';
import { TemplateModel } from '../../../../bip-template/bip-template.interface';
import { CasePlanStatusService } from '../../../../../shared/services/case-plan-status.service';
import { CasePlanStatusModel } from '../../../insights.interface';

@Component({
  selector: 'app-behavior-list',
  standalone: true,
  imports: [
    NzDescriptionsModule,
    NzButtonModule,
    SvgIconComponent,
    ConfirmDialogComponent,
    NoDataComponent,
    CommonModule,
    NzSwitchModule,
    FormsModule,
    ScreenerResultsGraphComponent,
    NzBreadCrumbComponent,
    NzBreadCrumbItemComponent,
    NzModalModule,
    ReportLabelPipe,
    DrawerHeaderComponent,
    NzToolTipModule,
    UserDatePipe,
    HelpTextComponent,
  ],
  templateUrl: './behavior-list.component.html',
  styleUrl: './behavior-list.component.scss',
})
export class BehaviorListComponent implements OnDestroy, OnInit, OnChanges {
  @Input() caseDetail = {} as CaseDetailModel;
  @Input() allowManageBehavior = true;
  @Input() allowManageAssessment = true;
  @Input() allowManageStrategy = true;
  @ViewChild('drawerTitleTpl') drawerTitleTpl!: TemplateRef<void>;
  @ViewChild('correctionNeededTpl') correctionNeededTpl!: TemplateRef<void>;
  @ViewChild('drawerBehaviorTitleTpl')
  drawerBehaviorTitleTpl!: TemplateRef<void>;
  @ViewChild('drawerBehaviorWizardTitleTpl')
  drawerBehaviorWizardTitleTpl!: TemplateRef<void>;
  @ViewChild('drawerTitleBehaviorStrategies')
  drawerTitleBehaviorStrategies!: TemplateRef<void>;
  @ViewChild('drawerTitleData') drawerTitleData!: TemplateRef<void>;
  @Output() listUpdated = new EventEmitter<void>();
  isDeleteDialogOpen = false;
  showOverlay = true;
  showLock = true;
  overlayMessage = 'Assessment incomplete – Click to start';

  isDeleting = false;
  selectedBehavior?: CaseBehaviour;
  private readonly destroy$ = new Subject<void>();
  chartImage: string | null = null;
  assessmentOldData: FunctionalAssessmentModelAnswer[] = [];
  labelEnum = {
    ASSESS_LABEL_OVERRIDE: LabelKey.ASSESS_LABEL_OVERRIDE,
    BEHAVIOR_DETAILS: LabelKey.BEHAVIOR_DETAILS,
    STRATEGIES_DETAILS: LabelKey.STRATEGIES_DETAILS,
  };

  isDefinitionModalVisible = false;
  selectedDefinition = '';

  assessLabel: string = LabelKey.BEHAVIOR_DETAILS;
  behaviorDetails: string = LabelKey.BEHAVIOR_DETAILS;
  stargyDetails: string = LabelKey.STRATEGIES_DETAILS;
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;

  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };

  bipTemplate: TemplateModel | null = null;
  tileSection = BipTileSection;
  includedTiles: Record<BipTileSection, boolean> = {
    [BipTileSection.Details]: true,
    [BipTileSection.Assessment]: true,
    [BipTileSection.Strategy]: true,
    [BipTileSection.Data]: true,
  };
  hardStopTiles: Record<BipTileSection, boolean> = {
    [BipTileSection.Details]: false,
    [BipTileSection.Assessment]: false,
    [BipTileSection.Strategy]: false,
    [BipTileSection.Data]: false,
  };
  requiredTiles: Record<BipTileSection, boolean> = {
    [BipTileSection.Details]: false,
    [BipTileSection.Assessment]: false,
    [BipTileSection.Strategy]: false,
    [BipTileSection.Data]: false,
  };

  constructor(
    private readonly drawerService: DrawerService,
    private readonly behaviorService: BehaviorService,
    private readonly commonService: CommonService,
    private readonly cd: ChangeDetectorRef,
    private readonly insightService: InsightsService,
    private readonly loaderService: LoaderService,
    private readonly reportLabelsService: ReportLabelsService,
    private readonly timeZoneService: TimeZoneService,
    private readonly bipTemplateGatingService: BipTemplateGatingService,
    private readonly casePlanStatusService: CasePlanStatusService
  ) {}

  casePlanStatus: CasePlanStatusModel | null = null;

  ngOnInit(): void {
    this.casePlanStatusService.status$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.casePlanStatus = status;
        this.cd.detectChanges();
      });
    this.loadTemplateGating(this.caseDetail?.bipTemplateId);
    this.insightService.assessment$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.assessmentOldData = res;
      });
    combineLatest({
      assessLabel: this.reportLabelsService.getLabelValue(
        LabelKey.ASSESS_LABEL_OVERRIDE
      ),
      behaviorDetails: this.reportLabelsService.getLabelValue(
        LabelKey.BEHAVIOR_DETAILS
      ),
      stargyDetails: this.reportLabelsService.getLabelValue(
        LabelKey.STRATEGIES_DETAILS
      ),
      fidelityCheckLabel: this.reportLabelsService.getLabelValue(
        LabelKey.FIDELITY_CHECK
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(labels => {
        this.assessLabel = labels.assessLabel ?? LabelKey.ASSESS_LABEL_OVERRIDE;
        this.behaviorDetails =
          labels.behaviorDetails ?? LabelKey.BEHAVIOR_DETAILS;
        this.fidelityCheckLabel =
          labels.fidelityCheckLabel ?? LabelKey.FIDELITY_CHECK;
        this.stargyDetails =
          labels.stargyDetails ?? LabelKey.STRATEGIES_DETAILS;
        this.labelEnum = {
          ASSESS_LABEL_OVERRIDE: this.assessLabel as LabelKey,
          BEHAVIOR_DETAILS: this.behaviorDetails as LabelKey,
          STRATEGIES_DETAILS: this.stargyDetails as LabelKey,
        };
      });

    this.caseDetail.caseBehaviors.forEach(behavior => {
      if (behavior.statusId === BehaviorStatus.INACTIVE) {
        behavior.isExpanded = false;
      } else {
        behavior.isExpanded = true;
      }
    });
  }

  // caseDetail is re-bound in place (this component stays mounted across a
  // case's getCaseDetail() refreshes), so a template change on the case
  // (bipTemplateId differing from the currently loaded template) would
  // otherwise never be picked up after the initial ngOnInit fetch.
  ngOnChanges(changes: SimpleChanges): void {
    const caseDetailChange = changes['caseDetail'];
    if (
      caseDetailChange &&
      !caseDetailChange.firstChange &&
      this.caseDetail?.bipTemplateId !== this.bipTemplate?.id
    ) {
      this.loadTemplateGating(this.caseDetail?.bipTemplateId);
    }
  }

  private loadTemplateGating(bipTemplateId: number | undefined): void {
    if (!bipTemplateId) {
      this.bipTemplate = null;
      this.updateIncludedTiles();
      return;
    }
    this.bipTemplateGatingService
      .getTemplate(bipTemplateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(template => {
        this.bipTemplate = template;
        this.updateIncludedTiles();
        this.cd.detectChanges();
      });
  }

  get generateRandomId(): string {
    const randomId = Math.floor(100000 + Math.random() * 900000); // NOSONAR - Safe for generating UI IDs, not security-sensitive
    return randomId.toString();
  }

  getCaseBehaviourSubTypes(behavior: CaseBehaviour): string[] {
    return behavior.behaviorCategoryTypes.map(c => c.behaviorTypeName) || [];
  }

  private updateIncludedTiles(): void {
    this.includedTiles = Object.values(BipTileSection).reduce(
      (tiles, tile) => {
        tiles[tile] = this.bipTemplateGatingService.isTileSectionIncluded(
          this.bipTemplate,
          tile
        );
        return tiles;
      },
      {} as Record<BipTileSection, boolean>
    );
    this.hardStopTiles = Object.values(BipTileSection).reduce(
      (tiles, tile) => {
        tiles[tile] = this.bipTemplateGatingService.isTileSectionHardStop(
          this.bipTemplate,
          tile
        );
        return tiles;
      },
      {} as Record<BipTileSection, boolean>
    );
    this.requiredTiles = Object.values(BipTileSection).reduce(
      (tiles, tile) => {
        tiles[tile] = this.bipTemplateGatingService.isTileSectionRequired(
          this.bipTemplate,
          tile
        );
        return tiles;
      },
      {} as Record<BipTileSection, boolean>
    );
  }

  getSectionHelpText(
    sectionName: BipTileSection | 'Questionnaires'
  ): string | null {
    return this.bipTemplateGatingService.getSectionHelpText(
      this.bipTemplate,
      sectionName
    );
  }

  private isTileCompleted(
    behavior: CaseBehaviour,
    tile: BipTileSection
  ): boolean {
    switch (tile) {
      case BipTileSection.Details:
        return !behavior.draft;
      case BipTileSection.Assessment:
        return this.isAssessmentCompleted(behavior);
      case BipTileSection.Strategy:
        return (
          !!behavior.appliedStrategyCount && behavior.appliedStrategyCount > 0
        );
      case BipTileSection.Data:
        return !!behavior.dataEnteredDate;
      default:
        return false;
    }
  }

  // Fixed Details -> Assessment -> Strategy -> Data order: a tile is enabled only if it's
  // included in the template AND every earlier included, Required-or-Hard-Stop tile is completed.
  // An included tile that's neither Required nor Hard-Stop never blocks the next one, regardless
  // of completion. Hard-Stop implies Required (auto-linked in the template builder), so the two
  // flags are checked together here.
  // Every tile, including Details, is withheld while the case's top-level questionnaire has any
  // pending Hard-Stop or Required item — those must be resolved before touching any behavior tile.
  // On top of that, Assessment/Strategy/Data are further withheld while the case-plan-status API
  // reports any pending Hard-Stop or Required item on this specific behavior — Details stays open
  // (once the case-level questionnaire is clear) since that's where those items get resolved.
  isTileEnabled(behavior: CaseBehaviour, tile: BipTileSection): boolean {
    if (!this.includedTiles[tile]) {
      return false;
    }
    if (!this.casePlanStatusService.isCaseGatingClear(this.casePlanStatus)) {
      return false;
    }
    if (
      tile !== BipTileSection.Details &&
      !this.casePlanStatusService.isBehaviorGatingClear(
        this.casePlanStatus,
        behavior.id
      )
    ) {
      return false;
    }
    const order = Object.values(BipTileSection);
    return order
      .slice(0, order.indexOf(tile))
      .every(
        earlierTile =>
          !this.includedTiles[earlierTile] ||
          (!this.hardStopTiles[earlierTile] &&
            !this.requiredTiles[earlierTile]) ||
          this.isTileCompleted(behavior, earlierTile)
      );
  }

  get isCaseQuestionnaireGatingClear(): boolean {
    return this.casePlanStatusService.isCaseGatingClear(this.casePlanStatus);
  }

  // Behavior Details status/color and the resume flow are driven by the
  // case-plan-status API's per-behavior pending flags, not by behavior.draft
  // — hasPendingHardStop takes priority (red) over hasPendingRequired
  // (orange), since that's where these items actually get resolved.
  private cachedCasePlanStatus: CasePlanStatusModel | null = null;
  private pendingFlagsByBehaviorId = new Map<
    number,
    { hardStop: boolean; required: boolean }
  >();

  private ensurePendingFlagsCache(): void {
    if (this.casePlanStatus === this.cachedCasePlanStatus) {
      return;
    }

    this.cachedCasePlanStatus = this.casePlanStatus;
    this.pendingFlagsByBehaviorId = new Map(
      (this.casePlanStatus?.behaviorList ?? []).map(b => [
        b.behaviorId,
        {
          hardStop: !!b.hasPendingHardStop,
          required: !!b.hasPendingRequired,
        },
      ])
    );
  }

  hasPendingHardStop(behavior: CaseBehaviour): boolean {
    this.ensurePendingFlagsCache();
    return this.pendingFlagsByBehaviorId.get(behavior.id)?.hardStop ?? false;
  }

  hasPendingRequired(behavior: CaseBehaviour): boolean {
    this.ensurePendingFlagsCache();
    return this.pendingFlagsByBehaviorId.get(behavior.id)?.required ?? false;
  }

  // The resume prompt's severity: orange only when the sole outstanding item
  // is Required (no Hard-Stop pending); red for a pending Hard-Stop, and red
  // as the default fallback when the prompt is shown for another reason
  // (e.g. Strategy/Data still incomplete) with no pending item at all.
  isResumeRequiredOnly(behavior: CaseBehaviour): boolean {
    return (
      !this.hasPendingHardStop(behavior) && this.hasPendingRequired(behavior)
    );
  }

  getFuncationsName(behaviorFunction: BehaviorFunction[]) {
    const result = behaviorFunction
      .map(item => item.behaviorFunctionName)
      .join(', ');
    return result;
  }
  editBehavior(behavior: CaseBehaviour, isDetailPage = false) {
    const behaviorDrawerRef = this.drawerService.openDrawer(
      DefineBehaviorComponent,
      this.drawerBehaviorTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: isDetailPage
          ? InsightMode.VIEW_BEHAVIOR
          : InsightMode.EDIT_BEHAVIOR,
        behaviorId: behavior.id,
      },
      DRAWER_IDS.DEFINE_BEHAVIOR
    );

    behaviorDrawerRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.listUpdated.emit();
        if (res?.isContinueProcessing) {
          this.openAssessmentFbaDrawer(behavior, true);
        }
      });
  }

  closeBehaviorDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR);
  }

  // Prototype entry point for the template-driven wizard (Challenging Behavior only).
  // Used by resume/details flows; keep legacy DefineBehavior drawer available for fallback.
  openDefineBehaviorWizard(behavior?: CaseBehaviour, isDetailPage = false) {
    const behaviorDrawerRef = this.drawerService.openDrawer(
      DefineBehaviorWizardComponent,
      this.drawerBehaviorWizardTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: behavior
          ? isDetailPage
            ? InsightMode.VIEW_BEHAVIOR
            : InsightMode.EDIT_BEHAVIOR
          : InsightMode.ADD_BEHAVIOR,
        behaviorId: behavior?.id,
      },
      DRAWER_IDS.DEFINE_BEHAVIOR_V2
    );

    behaviorDrawerRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.listUpdated.emit();
        if (res?.isContinueProcessing && behavior) {
          this.openAssessmentFbaDrawer(behavior, true);
        }
      });
  }

  closeDefineBehaviorWizard() {
    this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR_V2);
  }
  setOverlay(message: string, showLock = true) {
    this.overlayMessage = message;
    this.showLock = showLock;
    this.showOverlay = true;
  }

  hideOverlay() {
    this.showOverlay = false;
  }
  isAssessmentCompleted(behavior: CaseBehaviour): boolean {
    return (
      !behavior.draft &&
      behavior.behaviorFunction.length > 0 &&
      behavior.behaviorAssessmentGraph.length > 0
    );
  }

  openAssessmentFbaDrawer(behavior: CaseBehaviour, isDetailPage = false): void {
    if (!this.allowManageAssessment && !this.isAssessmentCompleted(behavior)) {
      return;
    }
    const fbaDrawerRef = this.drawerService.openDrawer(
      FbaAssessmentComponent,
      this.drawerTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        caseDetail: this.caseDetail,
        behavior: behavior,
        chartImage: behavior.chartImage,
        mode: isDetailPage ? InsightMode.EDIT : InsightMode.VIEW,
      },
      DRAWER_IDS.FBA
    );
    fbaDrawerRef.afterClose.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.listUpdated.emit();
      if (data?.assessmentData) {
        if (this.assessmentOldData && this.assessmentOldData.length > 0) {
          const payload = {
            caseBehaviorId: behavior.id,
            functionalAssessmentAnswers: this.assessmentOldData,
          };
          this.loaderService.show();
          const operation$ =
            this.insightService.submitBehaviouralAssessment(payload);
          operation$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.insightService.assessmentSubject.next([]);
              this.listUpdated.emit();
            },
            error: error => {
              this.loaderService.hide();
              this.handleError(error);
            },
          });
        }
      }
    });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  openManageBehaviorStrategiesDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        ManageBehaviorStrategiesComponent,
        this.drawerTitleBehaviorStrategies as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseDetail,
          behavior: behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseDetail.student.organizationId,
          isContinueProcessing: false,
        },
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.listUpdated.emit();
      });
  }
  openDataDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        InsightsDataComponent,
        this.drawerTitleData as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseDetail,
          behavior: behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseDetail.student.organizationId,
          caseBehaviorId: behavior.id,
        },
        DRAWER_IDS.MANAGE_DATA
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.listUpdated.emit();
      });
  }

  closeDataDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_DATA);
  }

  closeManageBehaviorStrategiesDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES);
  }

  deleteBehavior(behavior: CaseBehaviour) {
    this.isDeleteDialogOpen = true;
    this.selectedBehavior = behavior;
  }

  handleDelete(): void {
    this.isDeleting = true;
    this.behaviorService
      .deleteBehavior(Number(this.selectedBehavior?.id))
      .pipe(
        finalize(() => (this.isDeleting = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', 'Behavior deleted successfully');
          this.selectedBehavior = undefined;
          this.isDeleteDialogOpen = false;
          this.listUpdated.emit();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  handleCancel(): void {
    this.isDeleteDialogOpen = false;
    this.selectedBehavior = undefined;
  }

  onActiveInActiveBehavior(behavior: CaseBehaviour, event: Event) {
    behavior.statusId = event ? BehaviorStatus.ACTIVE : BehaviorStatus.INACTIVE;
    this.cd.detectChanges();
    this.behaviorService
      .updateBehaviorStatus(
        behavior.id,
        event ? BehaviorStatus.ACTIVE : BehaviorStatus.INACTIVE
      )
      .pipe(
        finalize(() => (this.isDeleting = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.cd.detectChanges();
          this.commonService.notify(
            'success',
            behavior.statusId === BehaviorStatus.ACTIVE
              ? 'Behavior Activated successfully'
              : 'Behavior InActivated successfully'
          );
          this.toggleBehaviorExpand(behavior);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          behavior.statusId = event
            ? BehaviorStatus.INACTIVE
            : BehaviorStatus.ACTIVE;
          this.toggleBehaviorExpand(behavior);
          this.cd.detectChanges();
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  openCorrectionNeededDrawer(behavior: CaseBehaviour): void {
    this.drawerService.openDrawer(
      FidelityCheckCorrectionNeededComponent,
      this.correctionNeededTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        type: 0,
        caseDetail: this.caseDetail,
        behavior: behavior,
      },
      DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
    );
  }
  toggleBehaviorExpand(behavior: CaseBehaviour): void {
    if (behavior.statusId === BehaviorStatus.INACTIVE) {
      behavior.isExpanded = false;
    } else {
      behavior.isExpanded = true;
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeFBADrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.FBA);
  }

  closeCorrectionNeededDrawer() {
    this.drawerService.closeDrawer(
      DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
    );
  }
  onChartImageGenerated(image: string, behavior: CaseBehaviour) {
    this.cd.detectChanges();
    behavior.chartImage = image;
  }

  getDaysAgoText(date: string | Date): string {
    const givenDate = new Date(date);
    const tranformedDate = this.timeZoneService.convertToUserTimeZone(date);
    const today = new Date();
    const timeDiff = today.getTime() - (tranformedDate ?? givenDate).getTime();

    let daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    if (daysAgo < 0) {
      daysAgo = 0;
    }
    return `${daysAgo} days ago`;
  }

  getDaysColorforDataEntry(date: string | Date): string {
    if (!date) {
      return '';
    }
    const givenDate = new Date(date);
    const today = new Date();
    const timeDiff = today.getTime() - givenDate.getTime();
    const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (daysAgo <= 14) return 'green-text';
    if (daysAgo <= 30) return 'orange-text';
    return 'red-text';
  }
  getDaysAgoColor(date: string | Date): string {
    if (!date) {
      return '';
    }
    const givenDate = new Date(date);
    const today = new Date();
    const timeDiff = today.getTime() - givenDate.getTime();
    const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (daysAgo <= 10) return '';
    if (daysAgo <= 20) return 'orange-text';
    return 'red-text';
  }

  showOperationalDefinitionModal(definition: string): void {
    this.selectedDefinition = definition;
    this.isDefinitionModalVisible = true;
  }

  handleModalClose(): void {
    this.isDefinitionModalVisible = false;
  }

  resumeProcess(label: string, behavior: CaseBehaviour): void {
    if (!this.isCaseQuestionnaireGatingClear) {
      return;
    }
    if (label === this.labelEnum.BEHAVIOR_DETAILS) {
      if (!this.allowManageBehavior) {
        return;
      }
      // Opens the template-driven wizard in edit mode so Resume lands on
      // whichever step is actually incomplete (resolveResumeIndex()), rather
      // than the legacy component's fixed page order. Old call kept
      // commented for easy revert: this.editBehavior(behavior, false);
      this.openDefineBehaviorWizard(behavior, false);
    }
    if (label === this.labelEnum.ASSESS_LABEL_OVERRIDE) {
      if (!this.allowManageAssessment) {
        return;
      }
      this.openAssessmentFbaDrawer(behavior, true);
    }
    if (label === this.labelEnum.STRATEGIES_DETAILS) {
      if (!this.allowManageStrategy) {
        return;
      }
      this.openManageBehaviorStrategiesDrawer(behavior);
    }
  }

  canResumeProcess(behavior: CaseBehaviour): boolean {
    if (!this.isCaseQuestionnaireGatingClear) {
      return false;
    }
    const label = this.getDynamicLabel(behavior);
    if (label === this.labelEnum.BEHAVIOR_DETAILS) {
      return this.allowManageBehavior;
    }
    if (label === this.labelEnum.ASSESS_LABEL_OVERRIDE) {
      return this.allowManageAssessment;
    }
    if (label === this.labelEnum.STRATEGIES_DETAILS) {
      return this.allowManageStrategy;
    }
    return false;
  }

  getDynamicLabel(behavior: CaseBehaviour): string {
    if (
      this.includedTiles[BipTileSection.Details] &&
      (this.hasPendingHardStop(behavior) || this.hasPendingRequired(behavior))
    ) {
      return this.behaviorDetails;
    }
    const sequence: { tile: BipTileSection; label: string }[] = [
      { tile: BipTileSection.Assessment, label: this.assessLabel },
      { tile: BipTileSection.Strategy, label: this.stargyDetails },
    ];
    const next = sequence.find(
      ({ tile }) =>
        this.includedTiles[tile] && !this.isTileCompleted(behavior, tile)
    );
    return next?.label ?? '';
  }
}
